import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Camera, ChevronDown, RotateCcw, Ruler, Check } from "lucide-react";
import FitKeyPass from "@/components/fittin/FitKeyPass";
import WalletAdd from "@/components/fittin/WalletAdd";
import FootSlider from "@/components/fittin/FootSlider";
import SizeStepper from "@/components/fittin/SizeStepper";
import FitPreference from "@/components/fittin/FitPreference";
import ComingSoonBadge from "@/components/fittin/ComingSoonBadge";
import { API, createFitKey, passSize, saveLocalFitKey } from "@/lib/fitkey";
import { startSession } from "@/lib/session";
import { formatSize } from "@/lib/sizes";

const UNITS = ["EU", "UK", "US"];
const GENDERS = [
  { key: "men", label: "Men" },
  { key: "women", label: "Women" },
];
const STEPS = [
  { key: "measure", label: "Measure" },
  { key: "reference", label: "Reference pair" },
  { key: "result", label: "Your FitKey" },
];

const labelCls = "text-[10.5px] uppercase tracking-[0.2em] text-neutral-500";
const inputCls =
  "mt-1.5 w-full rounded-xl border border-black/10 bg-white px-3.5 h-11 text-[14px] text-[#0A0A0A] placeholder:text-neutral-300 outline-none focus:border-black/50 transition-colors";
const motionProps = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

// CHECK-IN — the whole onboarding, no account.
// (a) measure the foot, (b) the reference pair, (c) the FitKey (Wallet pass).
// The reference shoe does not replace the foot: both are stored, and their
// difference is the user's fit preference.
export default function CheckIn() {
  const [brands, setBrands] = useState([]);
  const [brandsFailed, setBrandsFailed] = useState(false);

  const [step, setStep] = useState("measure");
  const [footLength, setFootLength] = useState(null);
  const [footWidth, setFootWidth] = useState(null);

  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [unit, setUnit] = useState("EU");
  const [size, setSize] = useState(null);
  const [gender, setGender] = useState(null);

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [fitkey, setFitkey] = useState(null);
  const [signIn, setSignIn] = useState("none"); // none | ok | failed

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/recommend/brands`, { timeout: 10000 })
      .then((r) => {
        if (!mounted) return;
        const list = Array.isArray(r?.data?.brands) ? r.data.brands : [];
        setBrands(list);
        if (!list.length) setBrandsFailed(true);
      })
      .catch(() => mounted && setBrandsFailed(true));
    return () => {
      mounted = false;
    };
  }, []);

  const brandName = useMemo(() => {
    const b = brands.find((x) => x.slug === brand);
    return b ? b.name : brand;
  }, [brand, brands]);

  const clearError = (key) => errors[key] && setErrors((cur) => ({ ...cur, [key]: undefined }));

  const validate = () => {
    const e = {};
    if (!brand.trim()) e.brand = "Pick the brand of your best-fitting pair.";
    if (!model.trim()) e.model = "Add the model, e.g. the name on the tongue or box.";
    if (size === null) e.size = "Set the size you wear in that pair.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev?.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const reference_size = formatSize(size); // fractions, e.g. "42⅔"
    const payload = {
      reference_brand: brand.trim(),
      reference_model: model.trim(),
      reference_size,
      reference_size_system: unit,
      ...(gender ? { gender } : {}),
      ...(footLength !== null ? { foot_length_cm: footLength } : {}),
      ...(footWidth !== null ? { foot_width_cm: footWidth } : {}),
    };
    const { fitkey: res, accessKey } = await createFitKey(payload);
    // Sign in right away with the one-time access key; it is never stored.
    let signed = "none";
    if (res.live && accessKey) {
      const s = await startSession({ fitkey_id: res.fitkey_id, access_key: accessKey });
      signed = s.ok ? "ok" : "failed";
    }
    const record = {
      ...res,
      brand_name: res.brand_name || brandName,
      reference_brand: payload.reference_brand,
      reference_model: payload.reference_model,
      reference_size,
      reference_unit: unit,
      gender: gender || null,
      created_at: new Date().toISOString(),
    };
    saveLocalFitKey(record);
    setFitkey(record);
    setSignIn(signed);
    setStep("result");
    setSubmitting(false);
  };

  const reset = () => {
    setFitkey(null);
    setErrors({});
    setSignIn("none");
    setStep("measure");
  };

  const previewSize = fitkey
    ? passSize(fitkey)
    : passSize({ reference_size: size === null ? "" : formatSize(size), reference_unit: unit });

  const stepIndex = STEPS.findIndex((s) => s.key === step);

  return (
    <section id="create-fitkey" data-testid="checkin-section" className="relative py-24 md:py-32 border-t border-black/5">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <div className="md:col-span-4">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
              <span className="h-px w-8 bg-black/40" />
              Check-in · Three steps
            </div>
          </div>
          <h2
            data-testid="checkin-headline"
            className="md:col-span-8 text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] font-medium leading-[1.02]"
          >
            Your foot, plus the shoe that fits you best.{" "}
            <span className="text-neutral-400">That’s your FitKey.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            {/* Step indicator */}
            <ol data-testid="checkin-steps" className="mb-5 flex flex-wrap items-center gap-2">
              {STEPS.map((s, i) => {
                const done = i < stepIndex;
                const active = i === stepIndex;
                return (
                  <li
                    key={s.key}
                    data-testid={`checkin-step-${s.key}`}
                    data-active={active}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] transition-all ${
                      active
                        ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                        : done
                        ? "bg-white text-[#0A0A0A] border-black/15"
                        : "bg-white/60 text-neutral-400 border-black/5"
                    }`}
                  >
                    <span
                      className={`grid place-items-center h-4 w-4 rounded-full text-[9.5px] ${
                        active ? "bg-white text-[#0A0A0A]" : done ? "bg-[#0A0A0A] text-white" : "bg-neutral-100 text-neutral-400"
                      }`}
                    >
                      {done ? <Check className="h-2.5 w-2.5" /> : i + 1}
                    </span>
                    {s.label}
                  </li>
                );
              })}
            </ol>

            <AnimatePresence mode="wait">
              {step === "measure" && (
                <motion.div key="measure" {...motionProps} data-testid="checkin-measure" className="glass-card rounded-3xl p-6 sm:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-[19px] font-medium tracking-tight">Measure your foot</h3>
                      <p className="mt-1.5 max-w-md text-[13px] text-neutral-500 leading-relaxed">
                        Stand on a sheet of paper, heel against the wall. Mark your longest toe and the widest
                        point, then measure. Optional, but it makes your size sharper.
                      </p>
                    </div>
                    <Ruler className="hidden sm:block h-5 w-5 text-neutral-300 shrink-0" />
                  </div>

                  <div className="mt-7 space-y-7">
                    <FootSlider
                      label="Foot length"
                      min={18}
                      max={33}
                      restAt={26}
                      value={footLength}
                      onChange={setFootLength}
                      hint="Heel to longest toe"
                      testid="checkin-foot-length"
                    />
                    <FootSlider
                      label="Foot width"
                      min={7}
                      max={13}
                      restAt={10}
                      value={footWidth}
                      onChange={setFootWidth}
                      hint="Widest point"
                      testid="checkin-foot-width"
                    />
                  </div>

                  <button
                    type="button"
                    disabled
                    aria-disabled
                    data-testid="checkin-scan-camera"
                    className="mt-7 w-full flex items-center justify-between gap-3 rounded-2xl border border-dashed border-black/15 bg-white/60 px-4 py-3.5 text-left cursor-not-allowed"
                  >
                    <span className="flex items-center gap-3">
                      <span className="grid place-items-center h-9 w-9 rounded-xl bg-neutral-100">
                        <Camera className="h-4 w-4 text-neutral-500" />
                      </span>
                      <span>
                        <span className="block text-[13.5px] text-neutral-700">Scan with your camera</span>
                        <span className="block text-[11.5px] text-neutral-400">Measure both values from one photo</span>
                      </span>
                    </span>
                    <ComingSoonBadge testid="checkin-scan-coming-soon" />
                  </button>

                  <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                    <button
                      type="button"
                      data-testid="checkin-measure-continue"
                      onClick={() => setStep("reference")}
                      className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0A0A] text-white px-5 py-3 text-[14px] hover:bg-neutral-800 transition-all active:scale-[0.98]"
                    >
                      Continue
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                    <button
                      type="button"
                      data-testid="checkin-measure-skip"
                      onClick={() => {
                        setFootLength(null);
                        setFootWidth(null);
                        setStep("reference");
                      }}
                      className="text-[12.5px] text-neutral-500 hover:text-[#0A0A0A] transition-colors"
                    >
                      Skip, I’ll measure later
                    </button>
                  </div>
                </motion.div>
              )}

              {step === "reference" && (
                <motion.form
                  key="reference"
                  {...motionProps}
                  onSubmit={submit}
                  noValidate
                  data-testid="checkin-form"
                  className="glass-card rounded-3xl p-6 sm:p-8"
                >
                  <h3 className="text-[19px] font-medium tracking-tight">Which shoe fits you best?</h3>
                  <p className="mt-1.5 text-[13px] text-neutral-500">The pair you’d buy again in the same size.</p>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls} htmlFor="checkin-brand">Brand</label>
                      {brandsFailed ? (
                        <input
                          id="checkin-brand"
                          data-testid="checkin-brand-input"
                          value={brand}
                          onChange={(e) => {
                            setBrand(e.target.value);
                            clearError("brand");
                          }}
                          placeholder="Brand of your pair"
                          className={inputCls}
                        />
                      ) : (
                        <div className="relative">
                          <select
                            id="checkin-brand"
                            data-testid="checkin-brand"
                            value={brand}
                            onChange={(e) => {
                              setBrand(e.target.value);
                              clearError("brand");
                            }}
                            className={`${inputCls} appearance-none pr-10 ${brand ? "" : "text-neutral-400"}`}
                          >
                            <option value="">{brands.length ? "Select brand" : "Loading brands…"}</option>
                            {brands.map((b) => (
                              <option key={b.slug} value={b.slug}>
                                {b.name}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 mt-[3px] -translate-y-1/2 h-4 w-4 text-neutral-400" />
                        </div>
                      )}
                      {errors.brand && <FieldError testid="checkin-error-brand">{errors.brand}</FieldError>}
                    </div>

                    <div>
                      <label className={labelCls} htmlFor="checkin-model">Model</label>
                      <input
                        id="checkin-model"
                        data-testid="checkin-model"
                        value={model}
                        onChange={(e) => {
                          setModel(e.target.value);
                          clearError("model");
                        }}
                        placeholder="e.g. the name on the box"
                        className={inputCls}
                      />
                      {errors.model && <FieldError testid="checkin-error-model">{errors.model}</FieldError>}
                    </div>

                    <div className="sm:col-span-2">
                      <div className="flex items-center justify-between">
                        <span className={labelCls}>Size you wear in it</span>
                        <div role="tablist" data-testid="checkin-unit-toggle" className="inline-flex rounded-full border border-black/10 bg-white p-0.5">
                          {UNITS.map((u) => (
                            <button
                              key={u}
                              type="button"
                              role="tab"
                              aria-selected={unit === u}
                              data-testid={`checkin-unit-${u}`}
                              onClick={() => {
                                if (u !== unit) setSize(null);
                                setUnit(u);
                              }}
                              className={`rounded-full px-3 py-1 text-[11.5px] transition-all ${
                                unit === u ? "bg-[#0A0A0A] text-white" : "text-neutral-600 hover:text-[#0A0A0A]"
                              }`}
                            >
                              {u}
                            </button>
                          ))}
                        </div>
                      </div>
                      <SizeStepper
                        unit={unit}
                        value={size}
                        invalid={!!errors.size}
                        onChange={(v) => {
                          setSize(v);
                          clearError("size");
                        }}
                        testid="checkin-size"
                      />
                      {errors.size && <FieldError testid="checkin-error-size">{errors.size}</FieldError>}
                    </div>

                    <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                      <span className={`${labelCls} mr-1`}>
                        Fit <span className="normal-case tracking-normal text-neutral-400">(optional)</span>
                      </span>
                      {GENDERS.map((g) => (
                        <button
                          key={g.key}
                          type="button"
                          data-testid={`checkin-gender-${g.key}`}
                          aria-pressed={gender === g.key}
                          onClick={() => setGender((cur) => (cur === g.key ? null : g.key))}
                          className={`rounded-full border px-3 py-1.5 text-[12.5px] transition-all ${
                            gender === g.key
                              ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                              : "bg-white text-[#0A0A0A] border-black/10 hover:bg-neutral-50"
                          }`}
                        >
                          {g.label}
                        </button>
                      ))}
                      {unit !== "EU" && (
                        <span className="text-[11.5px] text-neutral-400">UK and US sizes differ for men and women.</span>
                      )}
                    </div>

                    <div className="sm:col-span-2 border-t border-black/5 pt-4 flex flex-wrap items-center gap-2 text-[12px] text-neutral-500">
                      <Ruler className="h-3.5 w-3.5" />
                      {footLength !== null || footWidth !== null ? (
                        <span data-testid="checkin-measure-summary">
                          Your foot:{" "}
                          {[
                            footLength !== null ? `${footLength.toFixed(1)} cm long` : null,
                            footWidth !== null ? `${footWidth.toFixed(1)} cm wide` : null,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      ) : (
                        <span data-testid="checkin-measure-summary">No measurements yet.</span>
                      )}
                      <button
                        type="button"
                        data-testid="checkin-edit-measurements"
                        onClick={() => setStep("measure")}
                        className="underline underline-offset-2 hover:text-[#0A0A0A]"
                      >
                        {footLength !== null || footWidth !== null ? "Edit" : "Add them"}
                      </button>
                    </div>
                  </div>

                  <div className="mt-7 flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:justify-between">
                    <button
                      type="button"
                      data-testid="checkin-back"
                      onClick={() => setStep("measure")}
                      className="inline-flex items-center justify-center gap-1.5 px-2 py-2.5 text-[13px] text-neutral-500 hover:text-[#0A0A0A] transition-colors"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" /> Back
                    </button>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <span className="text-[11.5px] text-neutral-500 sm:order-first">No account, no password. The pass is your key.</span>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="checkin-submit"
                        className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0A0A] text-white px-5 py-3 text-[14px] hover:bg-neutral-800 transition-all active:scale-[0.98] disabled:opacity-60"
                      >
                        {submitting ? (
                          <>
                            <span className="h-3 w-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                            Creating your FitKey
                          </>
                        ) : (
                          <>
                            Create your FitKey
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.form>
              )}

              {step === "result" && fitkey && (
                <motion.div key="result" {...motionProps} data-testid="checkin-result">
                  <div className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-neutral-600">
                    <span className="inline-flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${fitkey.live ? "bg-emerald-500" : "bg-amber-400"}`} />
                      <span data-testid="checkin-result-status">
                        {fitkey.live ? "FitKey created" : "FitKey preview · saved on this device"}
                      </span>
                    </span>
                    {signIn === "ok" && (
                      <span data-testid="checkin-signed-in" className="text-neutral-500">· Signed in on this device</span>
                    )}
                  </div>
                  <h3 className="mt-4 text-3xl sm:text-4xl tracking-[-0.03em] font-medium leading-[1.05]">
                    Your FitKey is ready. <span className="text-neutral-400">Add it to your Wallet.</span>
                  </h3>
                  <p className="mt-4 max-w-lg text-[15px] text-neutral-600 leading-relaxed">
                    It carries your size across brands, based on your {fitkey.brand_name} {fitkey.reference_model} in{" "}
                    {fitkey.reference_unit} {fitkey.reference_size}. After you buy, open the pass to confirm the fit or
                    request a return.
                  </p>

                  {fitkey.live && (
                    <FitPreference fitkey={fitkey} testid="checkin-fit-preference" className="mt-6 max-w-lg" />
                  )}

                  <WalletAdd
                    appleUrl={fitkey.apple_wallet_url}
                    googleUrl={fitkey.google_wallet_url}
                    testid="checkin-wallet"
                    className="mt-7"
                  />

                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    {signIn === "ok" && (
                      <Link
                        to="/me"
                        data-testid="checkin-open-me"
                        className="group inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white px-4 py-2.5 text-[13px] hover:bg-neutral-800 transition-all"
                      >
                        Open My FitKey
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    )}
                    <Link
                      to={`/k/${encodeURIComponent(fitkey.fitkey_id)}`}
                      data-testid="checkin-open-pass"
                      className="group inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-4 py-2.5 text-[13px] hover:bg-neutral-50 transition-all"
                    >
                      Open the pass page
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                    <button
                      type="button"
                      data-testid="checkin-reset"
                      onClick={reset}
                      className="inline-flex items-center gap-1.5 px-2 py-2.5 text-[13px] text-neutral-500 hover:text-[#0A0A0A] transition-colors"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Use another pair
                    </button>
                  </div>
                  {signIn === "failed" && (
                    <div data-testid="checkin-signin-failed" className="mt-3 text-[12px] text-neutral-500">
                      Couldn’t sign you in on this device. Open your FitKey from your Wallet to sign in.
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right: the pass, live-filled */}
          <div className="lg:col-span-5">
            <div className="relative w-full max-w-[340px] mx-auto">
              <FitKeyPass
                size={previewSize}
                brand={fitkey ? fitkey.brand_name : brandName}
                model={fitkey ? fitkey.reference_model : model.trim()}
                fitkeyId={fitkey ? fitkey.fitkey_id : ""}
                testid={fitkey ? "checkin-result-pass" : "checkin-preview-pass"}
              />
              {!fitkey && (
                <div className="mt-4 text-center text-[11.5px] text-neutral-500">Your pass fills in as you go.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FieldError({ children, testid }) {
  return (
    <div data-testid={testid} className="mt-1.5 text-[12px] text-red-600">
      {children}
    </div>
  );
}
